// Deadlines: option expiries, cancellation dates, deposits, final numbers… kept on each project (project.deadlines)
// as { id, title, date (YYYY-MM-DD), kind, supplier, note, done, doneAt, doneBy }. Pure functions, used by the
// Deadlines page, the Dashboard and the menu, and tested in tests/unit/deadlines.test.mjs.
export const KINDS = [
  ['option', 'Option expires'], ['cancel', 'Cancellation deadline'], ['deposit', 'Deposit due'],
  ['numbers', 'Final numbers'], ['payment', 'Payment due'], ['other', 'Other']
];
export const kindLabel = k => (KINDS.find(x => x[0] === k) || KINDS[KINDS.length - 1])[1];
export const SOON_DAYS = 7;

import { dayNo, isoToday, isIso as validDate } from './dates.js';
export { isoToday, validDate };
// days from today until the date: 0 today, negative when it has passed
export const daysUntil = (date, today = isoToday()) => validDate(date) ? dayNo(date) - dayNo(today) : null;
export function whenText(date, today = isoToday()) {
  const n = daysUntil(date, today);
  if (n == null) return '';
  return n === 0 ? 'today' : n === 1 ? 'tomorrow' : n === -1 ? '1 day late' : n < 0 ? `${-n} days late` : `in ${n} days`;
}
const byDate = (a, b) => String(a.date).localeCompare(String(b.date)) || String(a.title).localeCompare(String(b.title));
// open deadlines split into overdue, the next SOON_DAYS days and later; done ones newest first
export function groupDeadlines(list = [], today = isoToday()) {
  const g = { overdue: [], soon: [], later: [], done: [] };
  for (const d of list) {
    if (d.done) { g.done.push(d); continue; }
    const n = daysUntil(d.date, today);
    (n == null ? g.later : n < 0 ? g.overdue : n <= SOON_DAYS ? g.soon : g.later).push(d);
  }
  g.overdue.sort(byDate); g.soon.sort(byDate); g.later.sort(byDate); g.done.sort((a, b) => -byDate(a, b));
  return g;
}
// how many need attention (overdue or within SOON_DAYS): the number on the menu
export const attention = (list = [], today = isoToday()) => list.filter(d => !d.done && (n => n != null && n <= SOON_DAYS)(daysUntil(d.date, today))).length;
// every open deadline across projects that is overdue or within `days`, soonest first
export function comingUp(projects = [], today = isoToday(), days = 14) {
  return projects.flatMap(p => (p.deadlines || []).filter(d => !d.done && (n => n != null && n <= days)(daysUntil(d.date, today)))
    .map(d => ({ ...d, projectId: p.id, projectName: p.name }))).sort(byDate);
}

// ── Calendar file (.ics, RFC 5545): one all-day event per open deadline, with reminders 3 days before and on the day
const esc = s => String(s || '').replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
// lines longer than 75 bytes continue on the next line after a space
function fold(line) {
  const out = []; let cur = '', bytes = 0;
  for (const ch of line) {
    const b = new TextEncoder().encode(ch).length;
    if (bytes + b > (out.length ? 74 : 75)) { out.push(cur); cur = ''; bytes = 0; }
    cur += ch; bytes += b;
  }
  out.push(cur);
  return out.join('\r\n ');
}
const ymd = iso => iso.replace(/-/g, '');
const nextDay = iso => { const t = new Date(Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10) + 1)); return t.toISOString().slice(0, 10); };
export function toIcs(items, { name = 'Prism deadlines', now = new Date() } = {}) {
  const stamp = now.toISOString().replace(/[-:]/g, '').replace(/\.\d+/, '');
  const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Glassbox//Prism//EN', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH', 'X-WR-CALNAME:' + esc(name)];
  for (const d of items) {
    if (d.done || !validDate(d.date)) continue;
    const title = `${kindLabel(d.kind)}: ${d.title || ''}${d.projectName ? ` (${d.projectName})` : ''}`;
    const desc = [d.supplier && 'Supplier: ' + d.supplier, d.note].filter(Boolean).join('\n');
    lines.push('BEGIN:VEVENT', `UID:${d.id}@glassbox-prism`, `DTSTAMP:${stamp}`, `DTSTART;VALUE=DATE:${ymd(d.date)}`, `DTEND;VALUE=DATE:${ymd(nextDay(d.date))}`,
      'SUMMARY:' + esc(title), ...(desc ? ['DESCRIPTION:' + esc(desc)] : []), 'TRANSP:TRANSPARENT',
      'BEGIN:VALARM', 'ACTION:DISPLAY', 'DESCRIPTION:' + esc(title), 'TRIGGER:-P3D', 'END:VALARM',
      'BEGIN:VALARM', 'ACTION:DISPLAY', 'DESCRIPTION:' + esc(title), 'TRIGGER:PT9H', 'END:VALARM', 'END:VEVENT');
  }
  lines.push('END:VCALENDAR');
  return lines.map(fold).join('\r\n') + '\r\n';
}
